import { useState, useEffect, useMemo} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Divider,
    Box,
    Chip,
    Paper,
    IconButton,
} from '@mui/material';
import { Calendar, Clock, CreditCard, Info, User, Star, MessageSquare, ThumbsUp } from 'lucide-react';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../../services/apiClient';

const statusColors: Record<string, any> = {
    NEW: 'secondary',
    STARTED: 'primary',
    WAITING: 'info',
    ENDED: 'default',
    FAILED: 'error',
    CANCELLED: 'error',
    EXPIRED: 'warning',
    UNPAID: 'warning',
    PAID: 'success',
    PENDING: 'warning',
    ACCEPTED: 'success',
    CREATED: 'info',
    COMPLETED: 'success',
    'VET ACCEPTED': 'success',
    'PAYMENT PENDING': 'warning',
    CONFIRMED: 'success',
    'LINK READY': 'info',
    'VET JOINED': 'info',
    'JOINED (YOU)': 'info',
    INPROGRESS: 'info',
    'CANCELLED (VET)': 'error',
    'CANCELLED (YOU)': 'error',
    'RESCHEDULE REQUESTED': 'warning',
    RESCHEDULED: 'info',
    'RESCHEDULE REJECTED': 'error',
    'NO SHOW (YOU)': 'error',
    'NO SHOW (VET)': 'error',
    'REFUND PENDING': 'warning',
    REFUNDED: 'success',
    'REFUND FAILED': 'error',
    'REFUND INITIATED': 'info',
};


const ReviewCard = ({ review, loadingReview }: any) => {
    if (loadingReview) {
        return (
            <div className="bg-gradient-to-b from-purple-50 to-white p-6 rounded-2xl shadow-md animate-pulse">
                {/* Header shimmer */}
                <div className="flex items-center justify-between mb-6">
                    <div className="h-6 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-full w-48 animate-shimmer"></div>
                    <div className="h-6 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-full w-32 animate-shimmer"></div>
                </div>

                {/* Comment shimmer */}
                <div className="h-24 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-2xl mb-6 animate-shimmer"></div>

                {/* Cards shimmer */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="h-16 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-xl animate-shimmer"></div>
                    <div className="h-16 bg-gradient-to-r from-purple-200 via-purple-100 to-purple-200 rounded-xl animate-shimmer"></div>
                </div>
            </div>
        );
    }


    if (!review) {
        return (
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-12 shadow-lg border-2 border-slate-200 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate-200/50 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" style={{ backgroundSize: '20px 20px', backgroundImage: 'linear-gradient(to right, #e2e8f0 1px, transparent 1px), linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)' }}></div>
                <div className="relative">
                    <div className="inline-flex p-4 bg-slate-200 rounded-full mb-4">
                        <MessageSquare className="w-12 h-12 text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-semibold text-lg">No review submitted yet</p>
                    <p className="text-slate-500 text-sm mt-2">The client hasn't provided feedback for this appointment</p>
                </div>
            </div>
        );
    }

    const rating = review.rating || 0;

    return (
        <div className="bg-white ">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-300/20 to-pink-300/20 rounded-full blur-3xl -mr-32 -mt-32 group-hover:scale-150 transition-transform duration-700" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-indigo-300/20 to-blue-300/20 rounded-full blur-2xl -ml-24 -mb-24 group-hover:scale-125 transition-transform duration-700" />

            <div className="relative">
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl shadow-lg group-hover:rotate-12 transition-transform duration-300">
                            <Star className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                Client Review
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">Feedback & Rating</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-2xl">
                        <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                                <Star
                                    key={i}
                                    className={`w-6 h-6 transition-all duration-300 ${i < rating
                                        ? 'text-amber-400 fill-amber-400 drop-shadow-[0_2px_4px_rgba(251,191,36,0.5)]'
                                        : 'text-gray-300'
                                        }`}
                                />
                            ))}
                        </div>
                        <div className="ml-2 flex flex-col items-end">
                            <span className="text-2xl font-black bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                                {rating}
                            </span>
                            <span className="text-xs text-gray-500 font-medium">out of 5</span>
                        </div>
                    </div>
                </div>

                {review.comment && (
                    <div className="bg-white/90 backdrop-blur-md rounded-2xl p-6 mb-6 shadow-lg border border-purple-100 group-hover:border-purple-300 transition-colors duration-300">
                        <div className="flex flex-col gap-3">
                            <div className="flex gap-3">
                                <MessageSquare className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                                <p className="text-gray-700 leading-relaxed text-base italic font-medium">
                                    "{review.comment}"
                                </p>
                            </div>

                            <div className="flex items-center gap-2 ml-8">
                                {/* <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Reviewed On</p> */}
                                <p className="text-sm font-semibold text-gray-900">
                                    {new Date(review.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {review.reviewer_name && (
                        <div className="flex items-center gap-4 bg-white/70 backdrop-blur-sm rounded-xl px-5 py-4 shadow-md border border-purple-100 hover:bg-white transition-all duration-300 group/item">
                            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg group-hover/item:scale-110 transition-transform duration-300">
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Reviewer</p>
                                <p className="text-sm font-semibold text-gray-900 mt-0.5">{review.reviewer_name}</p>
                            </div>
                        </div>
                    )}

                </div>

                {review.would_recommend !== undefined && (
                    <div className={`flex items-center gap-3 rounded-2xl px-5 py-4 border-2 shadow-lg transition-all duration-300 ${review.would_recommend
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 hover:shadow-green-200'
                        : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-300 hover:shadow-red-200'
                        }`}>
                        <div className={`p-2 rounded-lg ${review.would_recommend ? 'bg-green-500' : 'bg-red-500'}`}>
                            <ThumbsUp className={`w-5 h-5 text-white ${!review.would_recommend && 'rotate-180'}`} />
                        </div>
                        <p className={`text-sm font-bold ${review.would_recommend ? 'text-green-800' : 'text-red-800'}`}>
                            {review.would_recommend ? '✓ Would recommend this service' : '✗ Would not recommend'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const AppointmentViewDialog = ({ open, appointment, onClose }: any) => {
    const [review, setReview] = useState<any>(null);
    const [loadingReview, setLoadingReview] = useState(false);
    console.log('Appointment in Dialog:', JSON.stringify(appointment, null, 2));
    useEffect(() => {
        const controller = new AbortController();

        const fetchReview = async () => {
            if (!appointment?.id || !open) return;

            setLoadingReview(true);
            try {
                const { data } = await apiClient(
                    `v1/resource/ConsultReview?consult_id=${appointment.id}`,
                    {
                        method: 'GET',
                    }
                ) as any;

                // If the API doesn't support filtering, find the matching review
                const matchingReview = Array.isArray(data?.consult_reviews)
                    ? data.consult_reviews.find((r: any) => r.consult_id === appointment.id)
                    : data?.consult_review;

                setReview(matchingReview || null);
            } catch (error: any) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching review:', error);
                }
            } finally {
                setLoadingReview(false);
            }
        };

        fetchReview();

        return () => controller.abort();
    }, [appointment?.id, open]);

    // Memoized appointment data
    const appointmentData = useMemo(() => {
        if (!appointment) return null;

        const publisher = appointment.participants?.find((p: any) => p.role === 'publisher');
        const subscriber = appointment.participants?.find((p: any) => p.role === 'subscriber');

        return {
            publisher,
            subscriber,
            status: appointment.consult_current_status?.slug || '',
            scheduledDate: appointment.scheduled_at || '',
            createdDate: appointment.created_at || '',
        };
    }, [appointment]);

    const payment = appointment?.additional_info?.payment;
    const basePrice = payment.price || 0;
    let taxTotal = 0;
    const taxString = payment.taxes?.length
        ? payment.taxes.map((t: any) => {
            let amount = 0;
            if (t.type === 'percentage') {
                amount = (basePrice * parseFloat(t.rate)) / 100;
            } else if (t.type === 'fixed' || t.type === 'adhoc') {
                amount = parseFloat(t.rate);
            }
            taxTotal += amount;
            return `${t.name} (${t.rate}${t.type === 'percentage' ? '%' : ''}) = ₹${amount.toFixed(2)}`;
        }).join(', ')
        : 'None';
    const totalAmount = basePrice + taxTotal;


    if (!appointmentData) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle
                sx={{
                    backgroundColor: 'white',
                    color: 'black',
                    fontWeight: 'bold',
                    position: 'relative',
                    fontSize: '1.25rem',
                    letterSpacing: 1,
                }}
            >
                Appointment Details
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: '#000000', '&:hover': { color: '#666666' } }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Status Chip */}
                <Box mb={4} sx={{ textAlign: 'center' }}>
                    <Chip
                        label={appointment.consult_current_status?.name || 'Unknown'}
                        color={statusColors[appointmentData.status] || 'default'}
                        variant="filled"
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            px: 4,
                            py: 2,
                            borderRadius: '24px',
                            background: `linear-gradient(135deg, ${statusColors[appointmentData.status] || '#2196f3'}, #0d47a1)`,
                            color: '#fff',
                            boxShadow: `0 4px 20px ${statusColors[appointmentData.status] || '#2196f3'}88`,
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.08)',
                                boxShadow: `0 6px 25px ${statusColors[appointmentData.status] || '#2196f3'}cc`,
                            },
                        }}
                    />
                </Box>

                {/* Appointment Info Grid */}
                <Box
                    mb={5}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 4,
                        background: 'rgba(255,255,255,0.85)',
                        borderRadius: 4,
                        p: 4,
                        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
                    }}
                >
                    {[
                        ['Consult Code', appointment.consult_code, <Info size={18} color="#2196f3" />],
                        ['Consult Type', appointment.consult_type, <User size={18} color="#2196f3" />],
                        ['Scheduled At', appointment.scheduled_at, <Calendar size={18} color="#2196f3" />],
                        ['Consult Status', appointment.consult_current_status?.name, <Clock size={18} color="#2196f3" />],
                        ['Reason', appointment.reason, <Info size={18} color="#2196f3" />],
                        ['Created At', appointment.created_at, <Calendar size={18} color="#2196f3" />],
                        ['Virtual Provider', appointment.virtual_service_provider?.name, <User size={18} color="#2196f3" />],
                        ...(appointment.payment?.name
                            ? [['Payment Name', appointment.payment.name, <CreditCard size={18} color="#2196f3" />]]
                            : []),
                        ...(appointment.payment?.price !== undefined
                            ? [['Payment Price', `₹${appointment.payment.price}`, <CreditCard size={18} color="#2196f3" />]]
                            : []),
                    ].map(([label, value, icon]) => (
                        <Box
                            key={label}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                backgroundColor: 'white',
                                borderRadius: 3,
                                p: 2.5,
                                boxShadow: '0 4px 20px rgba(33, 150, 243, 0.15)',
                                transition: 'transform 0.3s ease, background-color 0.3s ease',
                                '&:hover': {
                                    backgroundColor: '#e3f2fd',
                                    transform: 'translateY(-3px)',
                                },
                            }}
                        >
                            {icon}
                            <Box>
                                <Typography variant="subtitle2" fontWeight="600" color="text.secondary">
                                    {label}
                                </Typography>
                                <Typography variant="body1" color={value ? 'text.primary' : 'text.disabled'}>
                                    {value || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ mb: 5, borderColor: '#90caf9' }} />

                {/* Participants Section */}
                <Typography
                    variant="h5"
                    gutterBottom
                    color="primary"
                    fontWeight="900"
                    sx={{ mb: 4, textAlign: 'center', letterSpacing: '0.1em', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}
                >
                    Participants
                </Typography>

                {appointment.participants?.length ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                            gap: 4,
                            mb: 5,
                        }}
                    >
                        {appointment.participants.map((p: any) => (
                            <Paper
                                key={p.id}
                                variant="outlined"
                                sx={{
                                    minWidth: 300,
                                    p: 4,
                                    borderRadius: 4,
                                    background: 'linear-gradient(135deg, #e3f2fd, #fff)',
                                    boxShadow: '0 10px 30px rgba(33, 150, 243, 0.1)',
                                    transition: 'all 0.3s ease',
                                    overflow: 'hidden',
                                    wordWrap: 'break-word',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 16px 40px rgba(33, 150, 243, 0.25)',
                                    },
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    color="primary"
                                    sx={{ mb: 3, borderBottom: '2px solid #2196f3', pb: 1 }}
                                >
                                    {p.participant_info?.name || 'Unnamed Participant'}
                                </Typography>

                                {[
                                    [
                                        p.role === 'subscriber' ? 'Pet Owner Email' : 'Email',
                                        p.role === 'subscriber' ? p.participant_info?.email || '-' : p.participant_info?.email || '-'
                                    ],
                                    [
                                        p.role === 'subscriber' ? 'Pet Owner Phone' : 'Phone',
                                        p.role === 'subscriber' ? p.participant_info?.phone || '-' : p.participant_info?.phone || '-'],
                                    [
                                        p.role === 'subscriber' ? 'Pet Gender' : 'Gender',
                                        p.role === 'subscriber' ? p.participant_info?.gender || '-' : p.participant_info?.gender || '-'],
                                    ['Role', p.role === 'subscriber' ? 'Pet Parent' : p.role === 'publisher' ? 'Veterinarian' : p.role],
                                    ['Status', p.participant_status?.name],
                                    ['Ref No.', p.ref_number],
                                ].map(([label, value]) => (
                                    <Box
                                        key={label}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1.5,
                                            backgroundColor: 'rgba(144,202,249,0.1)',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="primary"
                                            fontWeight="600"
                                            sx={{ whiteSpace: 'nowrap', pr: 1 }}
                                        >
                                            {label}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={value ? 'text.primary' : 'text.disabled'}
                                            sx={{ textAlign: 'right', overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '60%' }}
                                        >
                                            {value || '-'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Paper>
                        ))}
                    </Box>
                ) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', mb: 5 }}>
                        No participants found
                    </Typography>
                )}
                {appointment.additional_info?.payment && (
                    <Box mb={5}>
                        <Typography
                            variant="h5"
                            gutterBottom
                            color="primary"
                            fontWeight="900"
                            sx={{ mb: 3, textAlign: 'center', letterSpacing: '0.1em', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}
                        >
                            Payment Details
                        </Typography>

                        <Box
                            sx={{
                                width: '100%',
                                p: 3,
                                borderRadius: 3,
                                backgroundColor: '#e3f2fd',
                                boxShadow: '0 4px 20px rgba(33, 150, 243, 0.1)',
                            }}
                        >
                            <Typography variant="h6" fontWeight="700" sx={{ mb: 2 }}>
                                Payment Invoice
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {[
                                    ['Payment Code', appointment.additional_info.payment.code],
                                    ['Payment Name', appointment.additional_info.payment.name],
                                    ['Price', `₹${appointment.additional_info.payment.price}`],
                                ].map(([label, value], index) => (
                                    <Box key={label}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
                                            <Typography variant="body1" color="text.primary">{value}</Typography>
                                        </Box>
                                        {index < 2 && <Divider sx={{ borderColor: 'rgba(33, 150, 243, 0.3)' }} />}
                                    </Box>
                                ))}

                                {/* Taxes Section */}
                                <Box sx={{ py: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Taxes
                                    </Typography>
                                    {Array.isArray(appointment.additional_info.payment.taxes) ? (
                                        appointment.additional_info.payment.taxes.map((t: any, idx: number) => {
                                            let amount = 0;
                                            if (t.type === 'percentage') amount = (appointment.additional_info.payment.price * parseFloat(t.rate)) / 100;
                                            else if (t.type === 'fixed' || t.type === 'adhoc') amount = parseFloat(t.rate);

                                            return (
                                                <Box key={t.code}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                                        <Typography variant="body1" color="text.primary">
                                                            {t.name} ({t.rate}{t.type === 'percentage' ? '%' : ''})
                                                        </Typography>
                                                        <Typography variant="body1" color="text.primary">
                                                            ₹{amount.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    {idx < appointment.additional_info.payment.taxes.length - 1 && (
                                                        <Divider sx={{ borderColor: 'rgba(33, 150, 243, 0.3)' }} />
                                                    )}
                                                </Box>
                                            );
                                        })
                                    ) : (
                                        <Typography variant="body1" color="text.disabled">-</Typography>
                                    )}
                                </Box>

                                {/* Total */}
                                <Divider sx={{ borderColor: 'rgba(33, 150, 243, 0.3)', my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Total Amount</Typography>
                                    <Typography variant="h6" fontWeight="700">₹{totalAmount.toFixed(2)}</Typography>
                                </Box>
                            </Box>
                        </Box>




                    </Box>
                )}
                {/* Review Section */}
                <div className="mb-6">
                    <h2 className="text-3xl font-black text-center mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent">
                        Appointment Review
                    </h2>
                    <ReviewCard review={review} loadingReview={loadingReview} />
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AppointmentViewDialog;